import {
  getProjectSuggestion,
  type ProjectSuggestion,
} from 'api/agentSessions/projectSuggestions.api';
import { type ReactNode, useEffect, useRef, useState } from 'react';

// Mounted only when the explanation is opened. List/write responses carry the
// score and choices; this read retrieves the full saved context without inference.
export function ProjectSuggestionEvidence({
  entries,
  children,
  fetchSuggestion = getProjectSuggestion,
}: {
  entries: { sessionId: number; periodId: number; suggestion: ProjectSuggestion }[];
  children: (suggestions: Record<number, ProjectSuggestion>) => ReactNode;
  fetchSuggestion?: typeof getProjectSuggestion;
}) {
  const pending = useRef(new Map<string, ReturnType<typeof getProjectSuggestion>>());
  const [retry, setRetry] = useState(0);
  const key = JSON.stringify({ entries, retry });
  const [result, setResult] = useState<{
    key: string;
    suggestions?: Record<number, ProjectSuggestion>;
    error?: string;
  }>();
  useEffect(() => {
    let cancelled = false;
    const items = (JSON.parse(key) as { entries: typeof entries }).entries;
    if (items.every(item => item.suggestion.evidence)) return;
    const suggestions: Record<number, ProjectSuggestion> = {};
    let next = 0;
    setResult(undefined);
    const load = async () => {
      while (next < items.length && !cancelled) {
        const item = items[next++];
        const requestKey = `${item.sessionId}:${item.periodId}:${item.suggestion.fingerprint}`;
        let request = pending.current.get(requestKey);
        if (!item.suggestion.evidence && !request) {
          request = fetchSuggestion(item.sessionId, item.periodId).catch(error => {
            pending.current.delete(requestKey);
            throw error;
          });
          pending.current.set(requestKey, request);
        }
        const saved = item.suggestion.evidence ? item.suggestion : await request;
        if (!saved?.evidence || saved.fingerprint !== item.suggestion.fingerprint) {
          pending.current.delete(requestKey);
          throw new Error(
            'This suggestion changed. Reopen the project picker to see the latest result.'
          );
        }
        suggestions[item.periodId] = saved;
      }
    };
    Promise.all(Array.from({ length: Math.min(4, items.length) }, load))
      .then(() => {
        if (!cancelled) setResult({ key, suggestions });
      })
      .catch(() => {
        if (!cancelled)
          setResult({
            key,
            error: 'Could not load this suggestion’s context. It may have changed.',
          });
      });
    return () => {
      cancelled = true;
    };
  }, [key, fetchSuggestion]);
  if (entries.every(item => item.suggestion.evidence))
    return (
      <>{children(Object.fromEntries(entries.map(item => [item.periodId, item.suggestion])))}</>
    );
  if (result?.key === key && result.error)
    return (
      <div role="alert">
        {result.error}{' '}
        <button type="button" onClick={() => setRetry(value => value + 1)}>
          Try again
        </button>
      </div>
    );
  if (result?.key === key && result.suggestions) return <>{children(result.suggestions)}</>;
  return <p role="status">Loading suggestion context…</p>;
}
